import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { AnonymousSignUpFormWrapper } from '~/components/AnonymousSignUpForm/AnonymousSignUpFormWrapper';
import { AnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/AnonymousSignUpModal';
import { useAnonymousSignUpModalLogic } from '~/components/PublicLinkAnonymousSignUpModal/hooks/useAnonymousSignUpModalLogic';
import { AnonymousSignUpModalHeader } from '~/components/PublicLinkAnonymousSignUpModal/shared/AnonymousSignUpModalHeader';

export type PublicShowcaseAnonymousSignUpModalProps = {
  onLoggedIn?: () => void;
};

// The collection share link's "Require name/email to view" gate — the collection twin of
// PublicBoardAnonymousSignUpModal. Non-dismissible by construction: AnonymousSignUpModal renders no
// close affordance and passes no dismiss handler, so the only way past it is signing in.
export const PublicShowcaseAnonymousSignUpModal = memo(
  ({ onClose, onLoggedIn }: AirModalProps<PublicShowcaseAnonymousSignUpModalProps>) => {
    const { shortId, objectId } = useShortIdContext();
    const { asPath } = useRouter();

    const handleLoggedIn = useCallback(() => {
      onClose();
      onLoggedIn?.();
    }, [onClose, onLoggedIn]);

    const { onFullLoginClick, isOpen } = useAnonymousSignUpModalLogic({
      ssoCallbackData: { url: asPath },
      onLoginSuccess: handleLoggedIn,
    });

    return (
      <AnonymousSignUpModal isOpen={isOpen} data-testid="PUBLIC_COLLECTION_ANONYMOUS_SIGNUP_MODAL">
        <AnonymousSignUpModalHeader
          alt="Clouds with paper plane"
          imageSrc="/assets/illustrations/clouds-paper-plane.svg"
        />
        <AnonymousSignUpFormWrapper
          title="Files were shared with you!"
          description="Please enter your info to continue."
          onLoggedIn={handleLoggedIn}
          onFullLoginClick={onFullLoginClick}
          trackLocation="collection-share-link"
          objectId={objectId}
          shortId={shortId}
          objectType="collection"
        />
      </AnonymousSignUpModal>
    );
  },
);

PublicShowcaseAnonymousSignUpModal.displayName = 'PublicShowcaseAnonymousSignUpModal';
